import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, CreditCard, DollarSign, Plus, Trash2,
  ArrowDownCircle, ArrowUpCircle, CheckCircle, AlertCircle,
  Loader, RefreshCw, Clock, Shield, ExternalLink,
  ChevronRight, X, Wallet, TrendingUp, History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://financial-analyst-backend-production-7175.up.railway.app/api';

// ============================================
// PLAID LINK COMPONENT
// Uses Plaid Link to connect bank accounts
// ============================================
const PlaidLinkButton = ({ onSuccess, onExit, linkToken, receivedRedirectUri }) => {
  const [plaidReady, setPlaidReady] = useState(false);

  useEffect(() => {
    // Check if Plaid script is already loaded
    if (window.Plaid) {
      setPlaidReady(true);
      return;
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector('script[src*="plaid.com/link"]');
    if (existingScript) {
      // Script exists, wait for it to load
      if (window.Plaid) {
        setPlaidReady(true);
      } else {
        existingScript.addEventListener('load', () => setPlaidReady(true));
      }
      return;
    }

    // Load Plaid Link script
    const script = document.createElement('script');
    script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    script.async = true;
    script.onload = () => setPlaidReady(true);
    document.body.appendChild(script);

    // Don't remove script on unmount - it should persist
    return () => {
      // Keep script loaded for reuse
    };
  }, []);

  const openPlaidLink = () => {
    if (!window.Plaid || !linkToken) return;

    const config = {
      token: linkToken,
      onSuccess: (publicToken, metadata) => {
        console.log('Plaid success:', metadata);
        onSuccess(publicToken, metadata);
      },
      onExit: (err, metadata) => {
        console.log('Plaid exit:', err, metadata);
        if (onExit) onExit(err, metadata);
      },
      onEvent: (eventName, metadata) => {
        console.log('Plaid event:', eventName, metadata);
      }
    };
    // PRODUCTION: Pass this when user returns from OAuth bank redirect
    if (receivedRedirectUri) config.receivedRedirectUri = receivedRedirectUri;
    const handler = window.Plaid.create(config);

    handler.open();
  };

  return (
    <button
      onClick={openPlaidLink}
      disabled={!plaidReady || !linkToken}
      style={{
        width: '100%',
        padding: '1rem 1.5rem',
        background: !plaidReady || !linkToken 
          ? '#475569' 
          : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        border: 'none',
        borderRadius: '0.75rem',
        color: 'white',
        fontWeight: '700',
        fontSize: '1rem',
        cursor: !plaidReady || !linkToken ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        transition: 'all 0.2s'
      }}
    >
      {!plaidReady ? (
        <>
          <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
          Loading...
        </>
      ) : (
        <>
          <Building2 size={20} />
          Connect Bank Account
          <ExternalLink size={16} />
        </>
      )}
    </button>
  );
};

// ============================================
// LINKED ACCOUNT CARD
// ============================================
const LinkedAccountCard = ({ account, onDelete, onSelect, isSelected }) => (
  <div
    onClick={() => onSelect(account)}
    style={{
      background: isSelected ? 'rgba(99, 102, 241, 0.2)' : 'rgba(30, 41, 59, 0.6)',
      border: isSelected ? '2px solid #6366f1' : '1px solid #334155',
      borderRadius: '0.75rem',
      padding: '1rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem'
    }}
  >
    <div style={{
      width: '48px',
      height: '48px',
      borderRadius: '0.5rem',
      background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Building2 size={24} color="white" />
    </div>
    
    <div style={{ flex: 1 }}>
      <div style={{ color: '#e2e8f0', fontWeight: '600' }}>
        {account.bankName || 'Bank Account'}
      </div>
      <div style={{ color: '#94a3b8', fontSize: '0.85rem' }}>
        {account.accountType} •••• {account.last4}
      </div>
    </div>
    
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {account.status === 'APPROVED' ? (
        <span style={{
          background: 'rgba(34, 197, 94, 0.2)',
          color: '#22c55e',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.75rem',
          fontWeight: '600'
        }}>
          Active
        </span>
      ) : (
        <span style={{
          background: 'rgba(251, 191, 36, 0.2)',
          color: '#fbbf24',
          padding: '0.25rem 0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.75rem',
          fontWeight: '600'
        }}>
          Pending
        </span>
      )}
      
      {isSelected && <CheckCircle size={20} color="#6366f1" />}
      
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(account.id);
        }}
        style={{
          background: 'none',
          border: 'none',
          color: '#64748b',
          cursor: 'pointer',
          padding: '0.25rem'
        }}
      >
        <Trash2 size={16} />
      </button>
    </div>
  </div>
);

// ============================================
// TRANSFER MODAL
// ============================================
const TransferModal = ({ type, account, balance, onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isDeposit = type === 'deposit';
  const maxAmount = isDeposit ? 50000 : balance;

  const handleSubmit = async () => {
    const numAmount = parseFloat(amount);
    
    if (!numAmount || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (numAmount < 1) {
      setError('Minimum transfer is $1.00');
      return;
    }
    
    if (numAmount > maxAmount) {
      setError(isDeposit 
        ? 'Maximum deposit is $50,000 per transfer' 
        : `Insufficient funds. Available: $${balance.toFixed(2)}`
      );
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onSubmit(numAmount);
      onClose();
    } catch (err) {
      setError(err.message || 'Transfer failed');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = isDeposit 
    ? [100, 500, 1000, 5000] 
    : [100, 500, Math.min(1000, balance), balance];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      {/* FIXED: Added boxSizing and overflow to prevent input overflow */}
      <div style={{
        background: 'rgba(30, 41, 59, 0.98)',
        border: '1px solid #475569',
        borderRadius: '1rem',
        padding: '1.5rem',
        maxWidth: '450px',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{
            color: '#e2e8f0',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {isDeposit ? (
              <>
                <ArrowDownCircle size={24} color="#22c55e" />
                Deposit Funds
              </>
            ) : (
              <>
                <ArrowUpCircle size={24} color="#f59e0b" />
                Withdraw Funds
              </>
            )}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: '1.5rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* From/To */}
        <div style={{
          background: 'rgba(15, 23, 42, 0.6)',
          borderRadius: '0.75rem',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem',
            paddingBottom: '0.75rem',
            borderBottom: '1px solid #334155'
          }}>
            <div>
              <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                {isDeposit ? 'FROM' : 'FROM'}
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: '600' }}>
                {isDeposit ? `${account.bankName} •••• ${account.last4}` : 'InvestorQ Account'}
              </div>
            </div>
            <Building2 size={20} color="#64748b" />
          </div>
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                {isDeposit ? 'TO' : 'TO'}
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: '600' }}>
                {isDeposit ? 'InvestorQ Account' : `${account.bankName} •••• ${account.last4}`}
              </div>
            </div>
            <Wallet size={20} color="#64748b" />
          </div>
        </div>

        {/* Amount Input - FIXED: Added boxSizing to prevent overflow */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{
            color: '#94a3b8',
            fontSize: '0.85rem',
            marginBottom: '0.5rem',
            display: 'block'
          }}>
            Amount
          </label>
          <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
            <span style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#64748b',
              fontSize: '1.5rem',
              fontWeight: '600',
              pointerEvents: 'none'
            }}>
              $
            </span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              min="1"
              max={maxAmount}
              step="0.01"
              style={{
                width: '100%',
                padding: '1rem 1rem 1rem 2.5rem',
                background: '#1e293b',
                border: error ? '2px solid #ef4444' : '1px solid #475569',
                borderRadius: '0.5rem',
                color: '#e2e8f0',
                fontSize: '1.5rem',
                fontWeight: '600',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
          </div>
          {!isDeposit && (
            <p style={{ color: '#64748b', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              Available: ${balance?.toFixed(2) || '0.00'}
            </p>
          )}
        </div>

        {/* Quick Amount Buttons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '0.5rem',
          marginBottom: '1.5rem'
        }}>
          {quickAmounts.map((amt, idx) => (
            <button
              key={idx}
              onClick={() => setAmount(amt.toString())}
              style={{
                padding: '0.5rem',
                background: parseFloat(amount) === amt ? '#6366f1' : '#334155',
                border: 'none',
                borderRadius: '0.375rem',
                color: 'white',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              {idx === 3 && !isDeposit ? 'Max' : `$${amt}`}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid #ef4444',
            borderRadius: '0.5rem',
            padding: '0.75rem',
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={16} color="#ef4444" />
            <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>{error}</span>
          </div>
        )}

        {/* Timing Info */}
        <div style={{
          background: 'rgba(96, 165, 250, 0.1)',
          border: '1px solid rgba(96, 165, 250, 0.3)',
          borderRadius: '0.5rem',
          padding: '0.75rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Clock size={16} color="#60a5fa" />
          <span style={{ color: '#93c5fd', fontSize: '0.85rem' }}>
            {isDeposit 
              ? 'Deposits typically arrive in 2-3 business days' 
              : 'Withdrawals typically arrive in 3-5 business days'}
          </span>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !amount}
          style={{
            width: '100%',
            padding: '1rem',
            background: loading || !amount 
              ? '#475569'
              : isDeposit 
                ? 'linear-gradient(135deg, #22c55e, #16a34a)' 
                : 'linear-gradient(135deg, #f59e0b, #d97706)',
            border: 'none',
            borderRadius: '0.5rem',
            color: 'white',
            fontWeight: '700',
            fontSize: '1rem',
            cursor: loading || !amount ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem'
          }}
        >
          {loading ? (
            <>
              <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
              Processing...
            </>
          ) : (
            <>
              {isDeposit ? <ArrowDownCircle size={18} /> : <ArrowUpCircle size={18} />}
              {isDeposit ? 'Deposit' : 'Withdraw'} ${parseFloat(amount || 0).toFixed(2)}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================
// TRANSFER HISTORY
// ============================================
const TransferHistory = ({ transfers }) => {
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETE': return '#22c55e';
      case 'PENDING': return '#fbbf24';
      case 'FAILED':
      case 'CANCELED': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  if (!transfers || transfers.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '2rem',
        color: '#64748b'
      }}>
        <History size={40} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
        <p style={{ margin: 0 }}>No transfer history yet</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {transfers.map((transfer, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '0.75rem',
            background: 'rgba(15, 23, 42, 0.4)',
            borderRadius: '0.5rem'
          }}
        >
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: transfer.direction === 'INCOMING' 
              ? 'rgba(34, 197, 94, 0.2)' 
              : 'rgba(245, 158, 11, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {transfer.direction === 'INCOMING' ? (
              <ArrowDownCircle size={18} color="#22c55e" />
            ) : (
              <ArrowUpCircle size={18} color="#f59e0b" />
            )}
          </div>
          
          <div style={{ flex: 1 }}>
            <div style={{ color: '#e2e8f0', fontWeight: '500' }}>
              {transfer.direction === 'INCOMING' ? 'Deposit' : 'Withdrawal'}
            </div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>
              {new Date(transfer.createdAt).toLocaleDateString()}
            </div>
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <div style={{ 
              color: transfer.direction === 'INCOMING' ? '#22c55e' : '#f59e0b',
              fontWeight: '600'
            }}>
              {transfer.direction === 'INCOMING' ? '+' : '-'}${transfer.amount?.toFixed(2)}
            </div>
            <div style={{
              color: getStatusColor(transfer.status),
              fontSize: '0.75rem',
              fontWeight: '500'
            }}>
              {transfer.status}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============================================
// MAIN BANK FUNDING COMPONENT
// ============================================
const BankFunding = ({ userId }) => {
  const [accounts, setAccounts] = useState([]);
  const [oauthRedirectUri, setOauthRedirectUri] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [brokerAccount, setBrokerAccount] = useState(null);
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(null);
  const [activeTab, setActiveTab] = useState('accounts');

  // Fetch linked bank accounts
  const fetchAccounts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/broker/funding/ach?userId=${userId}`);
      const data = await response.json();
      if (data.success) {
        setAccounts(data.relationships || []);
        if (data.relationships?.length > 0 && !selectedAccount) {
          setSelectedAccount(data.relationships[0]);
        }
      }
    } catch (err) {
      console.error('Fetch accounts error:', err);
    }
  }, [userId, selectedAccount]);

  // Fetch transfer history
  const fetchTransfers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/broker/funding/transfers?userId=${userId}`);
      const data = await response.json();
      if (data.success) {
        setTransfers(data.transfers || []);
      }
    } catch (err) {
      console.error('Fetch transfers error:', err);
    }
  }, [userId]);

  // Fetch broker account (for balance)
  const fetchBrokerAccount = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/broker/account/details?userId=${userId}`);
      const data = await response.json();
      if (data.success) {
        setBrokerAccount(data.account);
      }
    } catch (err) {
      console.error('Fetch broker account error:', err);
    }
  }, [userId]);

  // Get Plaid link token
  const fetchLinkToken = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/plaid/link-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });
      const data = await response.json();
      if (data.linkToken) {
        setLinkToken(data.linkToken);
      }
    } catch (err) {
      console.error('Fetch link token error:', err);
    }
  }, [userId]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchAccounts(),
        fetchTransfers(),
        fetchBrokerAccount(),
        fetchLinkToken()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchAccounts, fetchTransfers, fetchBrokerAccount, fetchLinkToken]);

  // PRODUCTION: Detect OAuth redirect return from bank
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth_state_id')) {
      setOauthRedirectUri(window.location.href);
    }
  }, []);

  // Handle Plaid success
  const handlePlaidSuccess = async (publicToken, metadata) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('[BankFunding] Plaid success, exchanging token...');
      
      // Exchange public token for processor token
      const exchangeResponse = await fetch(`${API_URL}/plaid/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          publicToken,
          accountId: metadata.accounts[0].id 
        })
      });
      const exchangeData = await exchangeResponse.json();
      
      console.log('[BankFunding] Exchange response:', exchangeData);
      
      if (!exchangeResponse.ok) {
        throw new Error(exchangeData.error || 'Failed to exchange token');
      }
      
      if (exchangeData.processorToken) {
        console.log('[BankFunding] Creating ACH relationship...');
        
        // Create ACH relationship with Alpaca
        const achResponse = await fetch(`${API_URL}/broker/funding/ach/plaid`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId,
            processorToken: exchangeData.processorToken,
            isSandboxFallback: exchangeData.isSandboxFallback,
            accountInfo: exchangeData.accountInfo || {
              name: metadata.institution?.name || 'Bank Account',
              mask: metadata.accounts[0]?.mask || '0000',
              type: metadata.accounts[0]?.type || 'depository',
              subtype: metadata.accounts[0]?.subtype || 'checking'
            }
          })
        });
        const achData = await achResponse.json();
        
        console.log('[BankFunding] ACH response:', achData);
        
        if (achData.success) {
          await fetchAccounts();
          // Show success message
          if (achData.isSandbox) {
            console.log('[BankFunding] Sandbox mode - bank linked for testing');
          }
        } else {
          setError(achData.error || 'Failed to link bank account');
        }
      } else {
        setError(exchangeData.error || 'Failed to get processor token');
      }
    } catch (err) {
      console.error('[BankFunding] Error:', err);
      setError(err.message || 'Failed to connect bank');
    } finally {
      setLoading(false);
    }
  };

  // Handle transfer
  const handleTransfer = async (amount) => {
    const direction = showTransferModal === 'deposit' ? 'INCOMING' : 'OUTGOING';
    
    const response = await fetch(`${API_URL}/broker/funding/transfer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        relationshipId: selectedAccount.id,
        amount,
        direction
      })
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'Transfer failed');
    }
    
    // Refresh data
    await Promise.all([fetchTransfers(), fetchBrokerAccount()]);
  };

  // Delete ACH relationship
  const handleDeleteAccount = async (relationshipId) => {
    if (!window.confirm('Are you sure you want to remove this bank account?')) {
      return;
    }
    
    try {
      await fetch(`${API_URL}/broker/funding/ach/${relationshipId}?userId=${userId}`, {
        method: 'DELETE'
      });
      await fetchAccounts();
      if (selectedAccount?.id === relationshipId) {
        setSelectedAccount(null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading && accounts.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '4rem'
      }}>
        <Loader size={48} color="#6366f1" style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '1rem' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
        borderRadius: '1rem',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <h2 style={{
          color: 'white',
          margin: '0 0 0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <Wallet size={28} />
          Fund Your Account
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0 }}>
          Link a bank account to deposit or withdraw funds
        </p>
        
        {brokerAccount && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '0.75rem',
              padding: '1rem'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                Cash Balance
              </div>
              <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: '700' }}>
                ${brokerAccount.cash?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '0.75rem',
              padding: '1rem'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                Buying Power
              </div>
              <div style={{ color: 'white', fontSize: '1.5rem', fontWeight: '700' }}>
                ${brokerAccount.buyingPower?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid #ef4444',
          borderRadius: '0.5rem',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <AlertCircle size={18} color="#ef4444" />
          <span style={{ color: '#ef4444' }}>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              color: '#ef4444',
              cursor: 'pointer'
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '2px solid #334155',
        paddingBottom: '0.5rem'
      }}>
        {[
          { id: 'accounts', label: 'Bank Accounts', icon: Building2 },
          { id: 'history', label: 'Transfer History', icon: History }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: activeTab === tab.id ? '#0f766e' : 'transparent',
              border: 'none',
              borderRadius: '0.5rem 0.5rem 0 0',
              color: activeTab === tab.id ? 'white' : '#94a3b8',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Bank Accounts Tab */}
      {activeTab === 'accounts' && (
        <div>
          {/* Linked Accounts */}
          {accounts.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ 
                color: '#e2e8f0', 
                margin: '0 0 1rem',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                Linked Accounts
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {accounts.map((account, idx) => (
                  <LinkedAccountCard
                    key={idx}
                    account={account}
                    isSelected={selectedAccount?.id === account.id}
                    onSelect={setSelectedAccount}
                    onDelete={handleDeleteAccount}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Transfer Buttons */}
          {selectedAccount && selectedAccount.status === 'APPROVED' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <button
                onClick={() => setShowTransferModal('deposit')}
                style={{
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                  border: 'none',
                  borderRadius: '0.75rem',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <ArrowDownCircle size={20} />
                Deposit
              </button>
              <button
                onClick={() => setShowTransferModal('withdraw')}
                disabled={!brokerAccount?.cash || brokerAccount.cash <= 0}
                style={{
                  padding: '1rem',
                  background: brokerAccount?.cash > 0 
                    ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                    : '#475569',
                  border: 'none',
                  borderRadius: '0.75rem',
                  color: 'white',
                  fontWeight: '700',
                  fontSize: '1rem',
                  cursor: brokerAccount?.cash > 0 ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <ArrowUpCircle size={20} />
                Withdraw
              </button>
            </div>
          )}

          {/* Sandbox Test Funding Button */}
          {selectedAccount && (
            <div style={{
              background: 'rgba(251, 191, 36, 0.1)',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '0.75rem',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem',
                marginBottom: '0.5rem'
              }}>
                <Wallet size={18} color="#fbbf24" />
                <span style={{ color: '#fbbf24', fontWeight: '600', fontSize: '0.9rem' }}>
                  Sandbox Testing
                </span>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
                Add test funds instantly to your sandbox account for testing trades.
              </p>
              <button
                onClick={async () => {
                  try {
                    setLoading(true);
                    const response = await fetch(`${API_URL}/broker/funding/sandbox`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ userId, amount: 10000 })
                    });
                    const data = await response.json();
                    if (data.success) {
                      alert(`Success! Added $${data.amount?.toLocaleString() || '10,000'} to your account.`);
                      await fetchBrokerAccount();
                    } else {
                      setError(data.error || 'Failed to add test funds');
                      if (data.instructions) {
                        console.log('Manual funding instructions:', data.instructions);
                      }
                    }
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                  border: 'none',
                  borderRadius: '0.5rem',
                  color: '#1e293b',
                  fontWeight: '700',
                  fontSize: '0.9rem',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <DollarSign size={18} />
                Add $10,000 Test Funds
              </button>
            </div>
          )}

          {/* Connect Bank Button */}
          <div style={{
            background: 'rgba(30, 41, 59, 0.6)',
            border: '1px solid #334155',
            borderRadius: '0.75rem',
            padding: '1.5rem'
          }}>
            <h3 style={{ 
              color: '#e2e8f0', 
              margin: '0 0 0.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Plus size={20} />
              {accounts.length > 0 ? 'Add Another Bank' : 'Connect Your Bank'}
            </h3>
            <p style={{ color: '#94a3b8', margin: '0 0 1rem', fontSize: '0.9rem' }}>
              Securely link your bank account using Plaid
            </p>
            
            <PlaidLinkButton
              linkToken={linkToken}
              onSuccess={handlePlaidSuccess}
              onExit={() => {}}
              receivedRedirectUri={oauthRedirectUri}
            />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginTop: '1rem',
              color: '#64748b',
              fontSize: '0.8rem'
            }}>
              <Shield size={14} />
              Bank-level encryption • Never stores your credentials
            </div>
          </div>
        </div>
      )}

      {/* Transfer History Tab */}
      {activeTab === 'history' && (
        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid #334155',
          borderRadius: '0.75rem',
          padding: '1.5rem'
        }}>
          <TransferHistory transfers={transfers} />
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedAccount && (
        <TransferModal
          type={showTransferModal}
          account={selectedAccount}
          balance={brokerAccount?.cash || 0}
          onClose={() => setShowTransferModal(null)}
          onSubmit={handleTransfer}
        />
      )}

      {/* Disclosure */}
      <div style={{
        marginTop: '2rem',
        padding: '1rem',
        background: 'rgba(30, 41, 59, 0.4)',
        borderRadius: '0.5rem',
        fontSize: '0.75rem',
        color: '#64748b',
        lineHeight: '1.6'
      }}>
        <Shield size={14} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
        Brokerage services — broker-dealer partnership in progress.
        Bank account linking powered by Plaid Inc. Your credentials are never stored by InvestorQ.
      </div>
    </div>
  );
};

export default BankFunding;